import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Slideshow.css';
import BackgroundColorSelector from './BackgroundColorSelector';

function Slideshow() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [folders, setFolders] = useState([]);
  const [images, setImages] = useState([]);
  const [shownImages, setShownImages] = useState(new Set());
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [currentImages, setCurrentImages] = useState([null, null]);
  const [imageHistory, setImageHistory] = useState([]);
  const [showControls, setShowControls] = useState(true);
  const [transitionTime, setTransitionTime] = useState(6); // Default 6 seconds
  const controlsTimeoutRef = useRef(null);
  const folderInputRef = useRef(null);
  const timerRef = useRef(null);
  const appRef = useRef(null);
  const lastClickTimeRef = useRef(0);
  const foldersChangedRef = useRef(false);
  const [backgroundColor, setBackgroundColor] = useState('black');

  const MAX_IMAGES = 2500; // Unlimited for all users

  // Load cached folders from localStorage
  useEffect(() => {
    const loadCachedFolders = () => {
      try {
        const cachedFolders = localStorage.getItem('slideshow_folders');
        if (cachedFolders) {
          const parsedFolders = JSON.parse(cachedFolders);
          setFolders(parsedFolders);
        }
      } catch (error) {
        console.error('Error loading cached folders:', error);
      }
    };

    loadCachedFolders();
  }, []);

  // Save folders to localStorage whenever they change
  useEffect(() => {
    if (folders.length > 0) {
      localStorage.setItem('slideshow_folders', JSON.stringify(folders));
    } else {
      localStorage.removeItem('slideshow_folders');
    }
  }, [folders]);

  // Load transition time from localStorage
  useEffect(() => {
    const savedTransitionTime = localStorage.getItem('slideshow_transition_time');
    if (savedTransitionTime) {
      setTransitionTime(parseInt(savedTransitionTime, 10));
    }
  }, []);

  // Save transition time to localStorage
  useEffect(() => {
    localStorage.setItem('slideshow_transition_time', transitionTime.toString());
  }, [transitionTime]);

  const handleFolderSelect = useCallback(async (event) => {
    const files = Array.from(event.target.files);
    const folderImages = [];
    
    // Filter for image files
    const imageFiles = files.filter(file => 
      file.type.startsWith('image/') && 
      !file.name.startsWith('.')
    );

    // Get folder name from the first file's path
    const folderName = files[0]?.webkitRelativePath.split('/')[0] || 'Selected Folder';

    // Check if this is a reindex operation
    const isReindexing = folders.some(folder => folder.name === folderName);

    // If not reindexing, check folder limits
    if (!isReindexing) {
      // Check if we've reached the maximum number of folders
      if (folders.length >= 2) {
        alert('You can only select up to 2 folders.');
        return;
      }
    }

    // Check image limit
    if (imageFiles.length > MAX_IMAGES) {
      alert(`This folder contains more than ${MAX_IMAGES} images. Please select a smaller folder.`);
      return;
    }

    for (const file of imageFiles) {
      const imageUrl = URL.createObjectURL(file);
      folderImages.push({
        url: imageUrl,
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        webkitRelativePath: file.webkitRelativePath
      });
    }

    if (folderImages.length > 0) {
      if (isReindexing) {
        // Update existing folder
        setFolders(prevFolders => 
          prevFolders.map(folder => 
            folder.name === folderName 
              ? { ...folder, images: folderImages }
              : folder
          )
        );
        // Update images list
        setImages(prevImages => {
          const otherImages = prevImages.filter(img => !img.webkitRelativePath.startsWith(folderName));
          return [...otherImages, ...folderImages];
        });
      } else {
        // Add new folder
        setFolders(prevFolders => [...prevFolders, {
          name: folderName,
          images: folderImages
        }]);
        setImages(prevImages => [...prevImages, ...folderImages]);
      }
      setShownImages(new Set());
      setCurrentImages([null, null]);
      setImageHistory([]);
      foldersChangedRef.current = true;
    }
  }, [user, folders]);

  // Helper function to check if a folder has images loaded
  const hasImagesForFolder = (folderName) => {
    return images.some(img => img.webkitRelativePath.startsWith(folderName));
  };

  const removeFolder = async (folderName) => {
    foldersChangedRef.current = true;
    setFolders(prev => {
      const newFolders = prev.filter(folder => folder.name !== folderName);
      if (newFolders.length === 0) {
        localStorage.removeItem('slideshow_folders');
      }
      return newFolders;
    });
    setImages(prev => prev.filter(img => !img.webkitRelativePath.startsWith(folderName)));
  };

  const enterFullscreen = () => {
    const element = appRef.current;
    if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if (element.webkitRequestFullscreen) {
      element.webkitRequestFullscreen();
    } else if (element.msRequestFullscreen) {
      element.msRequestFullscreen();
    }
  };

  const exitFullscreen = () => {
    if (document.fullscreenElement || 
        document.webkitFullscreenElement || 
        document.msFullscreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  const handleDoubleClick = () => {
    if (!isPlaying) return;
    
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current;
    
    if (timeSinceLastClick < 300) {
      enterFullscreen();
    }
    
    lastClickTimeRef.current = now;
  };

  const handleExit = useCallback(() => {
    setIsPlaying(false);
    setIsAutoPlaying(false);
    clearInterval(timerRef.current);
    setCurrentImages([null, null]);
    setImageHistory([]);
    exitFullscreen();
  }, []);

  const getRandomImages = useCallback(() => {
    if (images.length === 0 || folders.length === 0) return [null];
    
    const imagesByFolder = {};
    images.forEach((img, index) => {
      const folder = img.webkitRelativePath.split('/')[0];
      if (!imagesByFolder[folder]) {
        imagesByFolder[folder] = [];
      }
      imagesByFolder[folder].push({ img, index });
    });

    const selectedImages = [];
    const selectedIndices = new Set();

    folders.forEach(folder => {
      const folderImages = imagesByFolder[folder.name] || [];
      const availableImages = folderImages.filter(({ index }) => !shownImages.has(index));
      
      if (availableImages.length > 0) {
        const randomIndex = Math.floor(Math.random() * availableImages.length);
        const { img } = availableImages[randomIndex];
        selectedImages.push(img.url);
        selectedIndices.add(availableImages[randomIndex].index);
      } else {
        // Reset shown images for this folder
        const folderIndices = folderImages.map(({ index }) => index);
        setShownImages(prev => {
          const newSet = new Set(prev);
          folderIndices.forEach(index => newSet.delete(index));
          return newSet;
        });
        // Try again with reset images
        return getRandomImages();
      }
    });

    // If no images are available, reset shown images and try again
    if (selectedImages.length === 0) {
      setShownImages(new Set());
      return getRandomImages();
    }

    setShownImages(prev => new Set([...prev, ...selectedIndices]));
    return selectedImages;
  }, [images, folders, shownImages]);

  const showNextImages = useCallback(() => {
    const newImages = getRandomImages();
    setCurrentImages(newImages);
    setImageHistory(prev => [...prev, newImages]);
  }, [getRandomImages]);

  const showPreviousImages = useCallback(() => {
    if (imageHistory.length > 1) {
      setImageHistory(prev => {
        const newHistory = [...prev];
        newHistory.pop();
        const previousImages = newHistory[newHistory.length - 1];
        setCurrentImages(previousImages);
        return newHistory;
      });
    }
  }, [imageHistory]);

  const handlePlay = () => {
    // Check if any folder needs reindexing
    const needsReindexing = folders.some(folder => !hasImagesForFolder(folder.name));
    if (needsReindexing) {
      return; // Don't start slideshow if images need to be indexed
    }
    
    setIsPlaying(true);
    setIsAutoPlaying(true);
    showNextImages();
    enterFullscreen();
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying(prev => !prev);
  };

  useEffect(() => {
    if (isPlaying && isAutoPlaying) {
      timerRef.current = setInterval(showNextImages, 6000);
    } else {
      clearInterval(timerRef.current);
    }

    return () => clearInterval(timerRef.current);
  }, [isPlaying, isAutoPlaying, showNextImages]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isPlaying) return;

      switch (event.key) {
        case 'ArrowRight':
          showNextImages();
          break;
        case 'ArrowLeft':
          showPreviousImages();
          break;
        case ' ':
          toggleAutoPlay();
          break;
        case 'Escape':
          handleExit();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPlaying, showNextImages, showPreviousImages, handleExit]);

  // Handle mouse movement to show/hide controls
  const handleMouseMove = useCallback(() => {
    if (!isPlaying) return;
    
    setShowControls(true);
    
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 2000);
  }, [isPlaying]);

  useEffect(() => {
    if (isPlaying) {
      window.addEventListener('mousemove', handleMouseMove);
      // Initial timeout to hide controls
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 2000);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isPlaying, handleMouseMove]);

  const handleTransitionTimeChange = (event) => {
    const newTime = parseInt(event.target.value, 10);
    setTransitionTime(newTime);
  };

  // Update the timer interval when transition time changes
  useEffect(() => {
    if (isPlaying && isAutoPlaying) {
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setCurrentImages(getRandomImages());
      }, transitionTime * 1000);
    }
  }, [transitionTime, isPlaying, isAutoPlaying, getRandomImages]);

  return (
    <div className="App" ref={appRef} onDoubleClick={handleDoubleClick} onMouseMove={handleMouseMove}>
      {!user && (
        <div className="fixed top-16 left-0 right-0 bg-primary text-white p-2 text-center z-50">
          <p>
            You are using free Snappy plan features. <button 
              onClick={() => navigate('/register')} 
              className="underline hover:text-accent"
            >
              Create an account
            </button> to unlock Snappy Plus features!
          </p>
        </div>
      )}
      {!isPlaying ? (
        <div className="App-header">
          <div className="folder-select">
            <input
              type="file"
              ref={folderInputRef}
              onChange={handleFolderSelect}
              webkitdirectory=""
              directory=""
              multiple
              style={{ display: 'none' }}
            />
            <button
              onClick={() => folderInputRef.current?.click()}
              className="select-folder-button"
            >
              Select Folders
            </button>
            {folders.length > 0 && (
              <>
                <button
                  onClick={handlePlay}
                  className="play-button"
                  disabled={folders.some(folder => !hasImagesForFolder(folder.name))}
                >
                  Play Slideshow
                </button>
                {folders.some(folder => !hasImagesForFolder(folder.name)) && (
                  <p className="text-sm text-red-500 mt-2">
                    Please reindex your folders before starting the slideshow
                  </p>
                )}
              </>
            )}
          </div>

          {folders.length > 0 && (
            <div className="folder-list">
              <h3>Selected Folders: ({folders.length}/2)</h3>
              <ul>
                {folders.map((folder, index) => (
                  <li key={index}>
                    <span>{folder.name}</span>
                    <div className="button-group">
                      {!hasImagesForFolder(folder.name) && (
                        <button
                          onClick={() => folderInputRef.current?.click()}
                          className="delete-button"
                          title="Reindex folder"
                        >
                          Reindex
                        </button>
                      )}
                      <button
                        onClick={() => removeFolder(folder.name)}
                        className="delete-button"
                        title="Remove folder"
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {folders.length > 0 && (
            <div className="transition-time-container">
              <label htmlFor="transition-time" className="transition-time-label">
                Transition Time: {transitionTime} seconds
                {!user && (
                  <span className="text-sm text-gray-600 block mt-1">
                    Create an account to customize transition time
                  </span>
                )}
              </label>
              <input
                type="range"
                id="transition-time"
                min="1"
                max="15"
                value={transitionTime}
                onChange={handleTransitionTimeChange}
                className="transition-time-slider"
                disabled={!user}
              />
            </div>
          )}

          {folders.length > 0 && (
            <div className="transition-time-container">
              <label className="transition-time-label">
                Background Color
                {!user && (
                  <span className="text-sm text-gray-600 block mt-1">
                    Create an account to customize background color
                  </span>
                )}
              </label>
              <BackgroundColorSelector
                selectedColor={backgroundColor}
                onChange={setBackgroundColor}
                disabled={!user}
              />
            </div>
          )}
        </div>
      ) : (
        <div 
          className={`slideshow-container ${showControls ? 'show-cursor' : ''}`}
          style={{ 
            backgroundColor: user && !backgroundColor.startsWith('url') ? backgroundColor : 'black',
            backgroundImage: user && backgroundColor.startsWith('url') ? backgroundColor : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className={`image-container ${folders.length === 1 ? 'single-folder' : ''}`}>
            {currentImages.map((image, index) => (
              image ? (
                <img 
                  key={index}
                  src={image} 
                  alt={`Slide ${index + 1}`} 
                  className={`slide-image ${folders.length === 1 ? 'single-image' : ''}`}
                />
              ) : null
            ))}
          </div>
          {showControls && (
            <div className="controls">
              <button onClick={showPreviousImages} className="nav-button">
                ← Previous
              </button>
              <button onClick={toggleAutoPlay} className="play-pause-button">
                {isAutoPlaying ? 'Pause' : 'Play'}
              </button>
              <button onClick={showNextImages} className="nav-button">
                Next →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Slideshow;